import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress
} from '@mui/material';
import { CreditCardOutlined, CloseOutlined } from '@mui/icons-material';

import { employeeUpdateCreditLimitApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifyError } from 'views/utilities/AlertToast';

const EditCreditLimitModal = ({ open, onClose, editingAccount, onSuccess }) => {
    const [limitForm, setLimitForm] = useState({
        email: '',
        employee_name: '',
        user_type: 'HR Employee',
        monthly_limit: '',
        status: 'Active'
    });
    const [isSavingLimit, setIsSavingLimit] = useState(false);

    useEffect(() => {
        if (editingAccount) {
            setLimitForm({
                email: editingAccount.email || '',
                employee_name: editingAccount.employee_name || editingAccount.name || '',
                user_type: editingAccount.user_type || editingAccount.role || 'HR Employee',
                monthly_limit: Math.floor(Number(editingAccount.monthly_limit ?? editingAccount.credit_limit ?? 0)).toString(),
                status: String(editingAccount.status || 'Active').toLowerCase() === 'active' ? 'Active' : 'Inactive'
            });
        }
    }, [editingAccount]);

    const handleSaveLimit = async (e) => {
        e.preventDefault();
        if (!limitForm.email.trim()) {
            globalNotifyError('Please enter a valid email');
            return;
        }
        const parsedLimit = parseInt(limitForm.monthly_limit, 10);
        if (isNaN(parsedLimit) || parsedLimit < 0) {
            globalNotifyError('Monthly limit must be a positive whole number');
            return;
        }

        setIsSavingLimit(true);
        try {
            const payload = {
                ...(editingAccount && {
                    id: editingAccount.id,
                    employee_id: editingAccount.employee_code || editingAccount.employee_id || editingAccount.id
                }),
                name: limitForm.employee_name || undefined,
                email: limitForm.email.trim(),
                monthly_limit: parsedLimit,
                credit_limit: parsedLimit,
                status: limitForm.status,
                user_type: limitForm.user_type
            };

            const response = await employeeUpdateCreditLimitApi(payload);

            if (response?.data?.success) {
                onClose();
                if (onSuccess) onSuccess();
            } else if (response?.data?.message) {
            } else {
                onClose();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error saving credit limit:', error);
            handleCatchError(error);
        } finally {
            setIsSavingLimit(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !isSavingLimit && onClose()}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#eff6ff', color: 'primary.main', display: 'flex' }}>
                        <CreditCardOutlined fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Edit Credit Limit
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Modify credit limit configuration for{' '}
                            {editingAccount?.employee_name || editingAccount?.name || editingAccount?.email || 'employee'}
                        </Typography>
                    </Box>
                </Stack>
                <IconButton size="small" onClick={onClose} disabled={isSavingLimit}>
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleSaveLimit}>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2.5}>
                        {/* Email */}
                        <TextField
                            label="Email Address"
                            required
                            fullWidth
                            type="email"
                            value={limitForm.email}
                            onChange={(e) => setLimitForm({ ...limitForm, email: e.target.value })}
                            disabled
                            helperText="Email cannot be modified for an existing account"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />

                        {/* Account Type */}
                        <FormControl fullWidth>
                            <InputLabel>Account Type</InputLabel>
                            <Select
                                label="Account Type"
                                value={limitForm.user_type}
                                onChange={(e) => setLimitForm({ ...limitForm, user_type: e.target.value })}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="HR Employee">HR Employee</MenuItem>
                                <MenuItem value="Corporate User">Corporate User</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Monthly Limit */}
                        <TextField
                            label="Monthly Limit (₹)"
                            required
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: 1 }}
                            value={limitForm.monthly_limit}
                            onKeyDown={(e) => {
                                if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            onChange={(e) => {
                                const val = e.target.value.split('.')[0].replace(/\D/g, '');
                                setLimitForm({ ...limitForm, monthly_limit: val });
                            }}
                            placeholder="50000"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />

                        {/* Status */}
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={limitForm.status}
                                onChange={(e) => setLimitForm({ ...limitForm, status: e.target.value })}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={isSavingLimit} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSavingLimit}
                        startIcon={isSavingLimit ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            bgcolor: '#1e40af'
                        }}
                    >
                        {isSavingLimit ? 'Saving...' : 'Save Limit'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

EditCreditLimitModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    editingAccount: PropTypes.object,
    onSuccess: PropTypes.func
};

export default EditCreditLimitModal;
